/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { inject as service } from '@ember/service';
import { action } from '@ember/object';
import { task } from 'ember-concurrency';

import AnalyticsService from 'cio/services/analytics';
import ApiService from 'cio/services/api';
import ApiErrorsService from 'cio/services/api-errors';
import AuthService from 'cio/services/auth';
import CollectionModel from 'cio/models/collection';
import ImportModel from 'cio/models/import';
import EnvironmentService from 'cio/services/environment';
import joinWords from 'cio/utils/join-words';
import { FlyMouseEvent } from '@customerio/fly-components/types/fly-components/events';
import AccountService from 'cio/services/account';
import { getUploadFn, SignResponse } from 'cio/utils/uploads';
import { SheetObject } from 'cio/utils/google-sheets';

const DEFAULT_MAX_FILE_SIZE = 100 * 1024 * 1024;
const COLLECTIONS_MAX_FILE_SIZE = 10 * 1024 * 1024;

const EXTENSION_TO_MIME_TYPE = {
  csv: 'text/csv',
  json: 'application/json',
};

interface UploadFileArgs {
  acceptedFileExtensions?: string[];
  addContentType: boolean;
  isDisabled: boolean;
  maxFileSize?: number;
  model: CollectionModel | ImportModel;
  onUpload: (options: { key: string; filename: string }) => unknown;
  onGoogleSheetImport: (sheet: SheetObject) => unknown;
  onChangeStart?: (type: 'replace' | 'delete') => unknown;
  onDelete: () => unknown;
  uploadType: 'peopleOrEvents' | 'collection' | 'segment';
}

export default class UploadFile extends Component<UploadFileArgs> {
  @service analytics!: AnalyticsService;
  @service api!: ApiService;
  @service apiErrors!: ApiErrorsService;
  @service auth!: AuthService;
  @service account!: AccountService;
  @service environment!: EnvironmentService;

  @tracked fileErrors?: string[];
  @tracked fileExtension!: 'csv' | 'json';

  googleSheetsImage = new URL('cio/assets/images/google-sheets.png', import.meta.url).toString();

  get maxFileSizeBytes() {
    // collections have a hard limit regardless of user's importMaxSizeBytes
    let isCollection = this.args.model instanceof CollectionModel;
    if (isCollection) {
      // custom limit from https://github.com/customerio/opsbugs/issues/1990
      if (this.auth?.account?.current.id === '54014') {
        return 128 * 1024 * 1024;
      } else {
        return COLLECTIONS_MAX_FILE_SIZE;
      }
    } else if (this.auth?.user?.importMaxSizeBytes) {
      return this.auth?.user?.importMaxSizeBytes;
    } else if (this.args.maxFileSize) {
      return this.args.maxFileSize;
    } else {
      return DEFAULT_MAX_FILE_SIZE;
    }
  }

  get maxFileSizeMB() {
    return Math.round(this.maxFileSizeBytes / 1024 / 1024);
  }

  get acceptedFileExtensions() {
    if (!this.args.acceptedFileExtensions?.length) {
      return '';
    }

    return joinWords(
      this.args.acceptedFileExtensions.map((ext) => ext.toUpperCase()),
      ',',
      ' or ',
    );
  }

  get acceptedMimeTypes() {
    if (!this.args.acceptedFileExtensions?.length) {
      return '*';
    }

    return this.args.acceptedFileExtensions
      .map((ext) => (ext === 'csv' || ext === 'json' ? EXTENSION_TO_MIME_TYPE[ext] : null))
      .compact()
      .join(',');
  }

  get onChangeStart() {
    return this.args.onChangeStart ?? (() => {});
  }

  signTask = task({ drop: true }, async (file: File) => {
    let { name, size } = file;

    try {
      let json = await this.args.model.sign({
        name: encodeURI(name),
        type: encodeURI(EXTENSION_TO_MIME_TYPE[this.fileExtension]),
        size: encodeURI(size.toString()),
      });

      await this.uploadTask.perform(file, json);
    } catch (e) {
      this.apiErrors.report(e);
    }
  });

  uploadTask = task({ drop: true }, async (file: File, json: SignResponse) => {
    let { uploadFn, data } = getUploadFn(json);

    try {
      let { key } = await uploadFn(file, data, {
        processData: (data: FormData) => {
          if (this.args.addContentType) {
            data.append('Content-Type', EXTENSION_TO_MIME_TYPE[this.fileExtension]);
          }
        },
      });

      this.args.onUpload({
        key,
        filename: file.name,
      });
    } catch (err) {
      this.apiErrors.report({ errors: [err] }, { externalAPIError: true });
    }
  });

  @action
  didSelectFile(file: File & { source: string }) {
    if (file.source === 'drag-and-drop') {
      this.analytics.track('import', 'upload_started', {
        source: 'from_disk',
        dragAndDrop: true,
      });
    }

    this.fileErrors = undefined;
    this.fileExtension = file.name.split('.').pop() as 'csv' | 'json';

    let { acceptedFileExtensions } = this.args;

    if (acceptedFileExtensions) {
      let splitFileName = file.name.split('.');

      if (splitFileName.length === 1 || !acceptedFileExtensions.includes(this.fileExtension)) {
        return (this.fileErrors = [`accepted file types are: ${acceptedFileExtensions.join(', ')}`]);
      }
    }

    if (file.size === 0) {
      return (this.fileErrors = ['selected file is empty']);
    }

    if (file.size > this.maxFileSizeBytes) {
      return (this.fileErrors = ['selected file is too big']);
    }

    return this.signTask.perform(file);
  }

  @action
  didClickChooseFile(evt: FlyMouseEvent<HTMLElement>) {
    // This fires twice because the event is attached to the label, and both the label and input trigger a click event
    if (evt.target.tagName === 'INPUT') {
      this.analytics.track('import', 'upload_started', {
        source: 'from_disk',
        dragAndDrop: false,
      });
    }
  }

  @action
  didClickImportSheet() {
    this.analytics.track('import', 'upload_started', { source: 'from_google_sheet' });
  }
}
